import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Image, Badge, Button, Pagination, Alert, Modal, Form } from "react-bootstrap";
import { AiFillEdit } from "react-icons/ai";
import { AiOutlineUser } from 'react-icons/ai'; // Default icon for students
import LeftNavManagement from "../LeftNavManagement";
import AdminHeader from "../AdminHeader";

// Define base URL for your API
const API_BASE_URL = 'https://brainrock.in/brainrock/backend';

const ManageStudent = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  
  // Student data state
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all'); // New state for payment status filter
  
  // Modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Selected students for deletion
  const [selectedStudents, setSelectedStudents] = useState([]);
  
  // Responsive check
  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
      setSidebarOpen(width >= 1024);
    };
    
    checkDevice();
    window.addEventListener("resize", checkDevice);
    return () => window.removeEventListener("resize", checkDevice);
  }, []);

  // Fetch student data
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/api/course-registration/`, {
          method: 'GET',
          credentials: 'include',
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch student data');
        }
        
        const data = await response.json();
        if (data.success) {
          // Process data to construct full image URLs
          const processedStudents = data.data.map(student => {
            const processedStudent = { ...student };
            
            // Format image URL
            if (student.profile_photo) {
              processedStudent.profile_photo = `${API_BASE_URL}${student.profile_photo}?t=${Date.now()}`;
            }
            
            return processedStudent;
          });
          
          setStudents(processedStudents);
        } else {
          throw new Error(data.message || 'Failed to fetch student data');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStudents();
  }, []);

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  
  // Function to format the application_for_course field
  const formatCourseList = (courses) => {
    if (!courses) return 'N/A';
    
    // If it's already a string, return it
    if (typeof courses === 'string') {
      return courses;
    }
    
    // If it's an array of objects with a name property
    if (Array.isArray(courses)) {
      if (courses.length > 0) {
        // Check if each item is an object with a name property
        if (typeof courses[0] === 'object' && courses[0] !== null) {
          // Extract the name from each object
          const courseNames = courses.map(course => 
            course.name || course.course_name || course.title || JSON.stringify(course)
          );
          return courseNames.join(', ');
        } else {
          // If it's an array of strings, join them
          return courses.join(', ');
        }
      } else {
        return 'N/A';
      }
    }
    
    // If it's an object, try to extract a meaningful property
    if (typeof courses === 'object' && courses !== null) {
      return courses.name || courses.course_name || courses.title || JSON.stringify(courses);
    }
    
    // Fallback
    return String(courses);
  };
  
  // Filter students based on search term and payment status
  const filteredStudents = students.filter((student) => {
    const lowerSearch = searchTerm.toLowerCase();
    const courseString = formatCourseList(student.application_for_course).toLowerCase();
    
    const matchesSearch = (
      student.candidate_name?.toLowerCase().includes(lowerSearch) ||
      student.email?.toLowerCase().includes(lowerSearch) ||
      student.mobile_no?.toLowerCase().includes(lowerSearch) ||
      courseString.includes(lowerSearch)
    );
    
    const matchesPaymentStatus = paymentStatusFilter === 'all' || student.payment_status === paymentStatusFilter;
    
    return matchesSearch && matchesPaymentStatus;
  });
  
  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredStudents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  
  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Handle view student details
  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setShowViewModal(true);
  };
  
  // Handle student selection for deletion
  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };
  
  // Handle select all students on current page
  const handleSelectAll = () => {
    if (selectedStudents.length === currentItems.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(currentItems.map(student => student.applicant_id));
    }
  };
  
  // Handle delete selected students
  const handleDeleteStudents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/course-registration/`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ applicant_id: selectedStudents }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete students');
      }
      
      const data = await response.json();
      if (data.success) {
        // Remove deleted students from the state
        setStudents(prev => prev.filter(student => !selectedStudents.includes(student.applicant_id)));
        setSelectedStudents([]);
        setShowDeleteModal(false);
      } else {
        throw new Error(data.message || 'Failed to delete students');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Open delete confirmation modal
  const openDeleteModal = () => {
    setShowDeleteModal(true);
  };
  
  // Close delete confirmation modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  // Handle payment status filter change
  const handlePaymentStatusFilterChange = (e) => {
    setPaymentStatusFilter(e.target.value);
    setCurrentPage(1); // Reset to first page when filter changes
  };

  // Get status badge variant
  const getStatusVariant = (status) => {
    switch(status) {
      case 'approved':
      case 'completed':
        return 'success';
      case 'rejected':
        return 'danger';
      case 'pending':
      default:
        return 'warning';
    }
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <LeftNavManagement
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        isMobile={isMobile}
        isTablet={isTablet}
      />

      {/* Main Content */}
      <div className="main-content">
        {/* Header */}
        <AdminHeader toggleSidebar={toggleSidebar} />

        {/* Dashboard Body */}
        <Container fluid className="dashboard-body">
          <div className="br-box-container">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2 className="mb-0">Manage Students</h2>
              <div className="d-flex gap-2">
                {selectedStudents.length > 0 && (
                  <Button  className="btn-delete"
                    variant="danger" 
                    onClick={openDeleteModal}
                    disabled={selectedStudents.length === 0}
                  >
                    Delete Selected ({selectedStudents.length})
                  </Button>
                )}
                <div style={{ width: '200px' }}>
                  <Form.Select
                    value={paymentStatusFilter}
                    onChange={handlePaymentStatusFilterChange}
                  >
                    <option value="all">All Payment Status</option>
                    <option value="pending">Pending</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </Form.Select>
                </div>
                <div style={{ width: '300px' }}>
                  <input
                    type="text"
                    placeholder="Search by name, email, phone, or course..."
                    className="form-control"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
            </div>
            
            {error && (
              <Alert variant="danger" className="mb-4">
                Error: {error}
              </Alert>
            )}
            
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-2">Loading student data...</p>
              </div>
            ) : (
              <>
                <Row className="mt-3">
                  <div className="col-md-12">
                    <table className="temp-rwd-table">
                      <tbody>
                        <tr>
                          <th>
                            <Form.Check 
                              type="checkbox"
                              checked={selectedStudents.length === currentItems.length && currentItems.length > 0}
                              onChange={handleSelectAll}
                              label=""
                            />
                          </th>
                          <th>S.No</th>
                          <th>Name</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Course</th>
                          <th>Mode</th>
                          <th>Status</th>
                          <th>Course Fee</th>
                          <th>Payment Status</th>
                          <th>Date</th>
                          <th>Action</th>
                        </tr>
                        
                        {currentItems.length > 0 ? (
                          currentItems.map((student, index) => (
                            <tr key={student.id}>
                              <td data-th="Select">
                                <Form.Check 
                                  type="checkbox"
                                  checked={selectedStudents.includes(student.applicant_id)}
                                  onChange={() => handleSelectStudent(student.applicant_id)}
                                  label=""
                                />
                              </td>
                              <td data-th="S.No">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                              <td data-th="Name">{student.candidate_name}</td>
                              <td data-th="Email">{student.email}</td>
                              <td data-th="Phone">{student.mobile_no}</td>
                              <td data-th="Course">{formatCourseList(student.application_for_course)}</td>
                              <td data-th="Mode">{student.course_mode}</td>
                              <td data-th="Status">
                                <span className={`badge bg-${getStatusVariant(student.course_status)} me-2`}>
                                  {student.course_status}
                                </span>
                              </td>
                              <td data-th="Course Fee">₹{parseFloat(student.course_fee).toFixed(2)}</td>
                              <td data-th="Payment Status">
                                <span className={`badge bg-${student.payment_status === 'completed' ? 'success' : 'warning'}`}>
                                  {student.payment_status}
                                </span>
                              </td>
                              <td data-th="Date">{formatDate(student.created_at)}</td>
                              <td data-th="Action">
                                <Button 
                                  variant="primary" 
                                  size="sm" 
                                  onClick={() => handleViewStudent(student)}
                                >
                                  View
                                </Button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="12" className="text-center">
                              No student data available.
                            </td>
                          </tr>
                        )}
                      </tbody>
                      <tfoot>
                        <tr style={{ backgroundColor: '#f0f8ff', fontWeight: 'bold', borderTop: '2px solid #0066cc' }}>
                          <td colSpan="8" className="text-end pe-3" style={{ color: '#333' }}>
                            <strong>Payment Summary:</strong>
                          </td>
                          <td colSpan="4" style={{ paddingLeft: '20px' }}>
                            <div style={{ fontSize: '13px', lineHeight: '1.8' }}>
                              <div style={{ color: '#27ae60', fontWeight: '600' }}>
                                ✓ Completed: {filteredStudents.filter(s => s.payment_status === 'completed').length} | ₹{parseFloat(filteredStudents.filter(s => s.payment_status === 'completed').reduce((sum, s) => sum + (parseFloat(s.course_fee) || 0), 0)).toFixed(2)}
                              </div>
                              <div style={{ color: '#e74c3c', fontWeight: '600' }}>
                                ✗ Unsuccessful: {filteredStudents.filter(s => s.payment_status !== 'completed').length} | ₹{parseFloat(filteredStudents.filter(s => s.payment_status !== 'completed').reduce((sum, s) => sum + (parseFloat(s.course_fee) || 0), 0)).toFixed(2)}
                              </div>
                              <div style={{ marginTop: '8px', borderTop: '1px solid #bdc3c7', paddingTop: '8px', color: '#0066cc', fontWeight: '700' }}>
                                Total: ₹{parseFloat(filteredStudents.reduce((sum, s) => sum + (parseFloat(s.course_fee) || 0), 0)).toFixed(2)}
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </Row>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-center mt-4">
                    <Pagination>
                      <Pagination.Prev 
                        onClick={() => handlePageChange(currentPage - 1)} 
                        disabled={currentPage === 1}
                      />
                      <Pagination.Item 
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      >
                        1
                      </Pagination.Item>
                      {currentPage > 3 && (
                        <Pagination.Ellipsis disabled />
                      )}
                      {[...Array(totalPages).keys()].map(page => {
                        const pageNum = page + 1;
                        if (pageNum === 1 || pageNum === totalPages) return null;
                        if (pageNum < currentPage - 1 || pageNum > currentPage + 1) return null;
                        return (
                          <Pagination.Item 
                            key={pageNum}
                            active={pageNum === currentPage}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </Pagination.Item>
                        );
                      })}
                      {currentPage < totalPages - 2 && (
                        <Pagination.Ellipsis disabled />
                      )}
                      {totalPages > 1 && (
                        <Pagination.Item 
                          onClick={() => handlePageChange(totalPages)}
                          disabled={currentPage === totalPages}
                        >
                          {totalPages}
                        </Pagination.Item>
                      )}
                      <Pagination.Next 
                        onClick={() => handlePageChange(currentPage + 1)} 
                        disabled={currentPage === totalPages}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>
        </Container>
      </div>
      
      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={closeDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {selectedStudents.length} selected student(s)?</p>
          <p>This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteStudents}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* View Student Details Modal */}
      <Modal show={showViewModal} onHide={() => setShowViewModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Student Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedStudent && (
            <div>
              <Row>
                <Col md={6} className="mb-3">
                  <div className="d-flex align-items-center">
                    {/* Display profile photo or a default icon */}
                    {selectedStudent.profile_photo ? (
                      <img 
                        src={selectedStudent.profile_photo} 
                        alt={selectedStudent.candidate_name} 
                        style={{ width: '120px', height: '120px', marginRight: '20px', objectFit: 'cover', borderRadius: '10px' }} 
                      />
                    ) : (
                      <AiOutlineUser size={120} style={{ marginRight: '20px' }} />
                    )}
                    <div>
                      <h5>{selectedStudent.candidate_name}</h5>
                      <p className="text-muted">ID: {selectedStudent.applicant_id}</p>
                    </div>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <p><strong>Application Date:</strong> {formatDate(selectedStudent.created_at)}</p>
                  <p><strong>Course Status:</strong> 
                    <span className={`badge bg-${getStatusVariant(selectedStudent.course_status)} ms-2`}>
                      {selectedStudent.course_status}
                    </span>
                  </p>
                </Col>
              </Row>
              <Row>
                <Col md={6} className="mb-3">
                  <p><strong>Email:</strong> {selectedStudent.email}</p>
                  <p><strong>Mobile:</strong> {selectedStudent.mobile_no}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <p><strong>Date of Birth:</strong> {selectedStudent.date_of_birth}</p>
                  <p><strong>Highest Education:</strong> {selectedStudent.highest_education}</p>
                </Col>
              </Row>
              <Row>
                <Col md={6} className="mb-3">
                  <p><strong>Course Fee:</strong> ₹{parseFloat(selectedStudent.course_fee).toFixed(2)}</p>
                </Col>
                <Col md={6} className="mb-3">
                  <p><strong>Payment Status:</strong> 
                    <span className={`badge bg-${selectedStudent.payment_status === 'completed' ? 'success' : 'warning'} ms-2`}>
                      {selectedStudent.payment_status}
                    </span>
                  </p>
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mb-3">
                  <p><strong>Applied Course:</strong> {formatCourseList(selectedStudent.application_for_course)}</p>
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mb-3">
                  <p><strong>School/College:</strong> {selectedStudent.school_college_name}</p>
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mb-3">
                  <p><strong>Address:</strong> {selectedStudent.address}</p>
                </Col>
              </Row>
              <Row>
                <Col md={12} className="mb-3">
                  <p><strong>Guardian Name:</strong> {selectedStudent.guardian_name}</p>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageStudent;